// Дашборд здоровʼя — екран за замовчуванням (docs/admin_panel.md).
//
// Проби збирає overseer півгодинними відрами в health_samples; тут ми їх
// лише малюємо. Смужка — тиждень: 336 відер, зелене/червоне/сіре, а деталі
// падіння — у підказці на комірці.
import { Fragment } from "react";
import { api } from "../api.js";
import { go } from "../app.jsx";
import { BeatRow, BeatScale } from "../charts.jsx";
import { ServerSection } from "./Server.jsx";
import { ApiTech } from "./Analytics.jsx";
import { Card, Empty, Kpi, LOSS_BAD_PCT, RangePicker, UPLINK, fmt, rangeLabel, useData, useRange } from "../ui.jsx";

// Підсумок по точці: зелена крапка лише тоді, коли зелене все, що ми вміємо
// перевірити. Дивитись доводилось у пʼять рядків одразу — тепер відповідь
// одна, а подробиці під нею (прохання власника 24.09.2026).
//
// Відеопотік у підсумок не входить, поки камери немає фізично: інакше точка
// світилась би червоним цілодобово й підсумок нічого не означав би. Свій
// рядок він має, і він там чесно червоний.
function pointOk(p) {
  if (p.ok !== true) return p.ok;               // мовчить або ще не озивалась
  const m = p.telemetry?.pi?.metrics ?? {};
  const checks = [
    p.monitor ? p.monitor.ok : null,
    // Розетка — запасний вихід для зависання: малина працює, але без
    // робочої розетки перезавантажити її живленням нема чим (25.10.2026).
    p.plug ? p.plug.ok : null,
    m.monitor_on === false ? false : null,
    m.root_ro === true ? false : null,
    m.kiosk_fps !== undefined && (m.kiosk_fps === null || Number(m.kiosk_fps) <= 0) ? false : null,
    Number.isFinite(m.disk_free_mb) && m.disk_free_mb < 300 ? false : null,
    // Ті самі пороги, що в overseer (checks.js, DEVICE_CHECKS): підсумок на
    // дашборді має гаснути рівно тоді, коли приходить алерт, — інакше
    // «зелено, але в телеграмі поломка» (24.09.2026).
    m.usb_ok === false ? false : null,
    Number.isFinite(m.temp_c) && m.temp_c >= 80 ? false : null,
    m.mem_total_mb > 0 && m.mem_used_mb / m.mem_total_mb > 0.92 ? false : null,
    Number.isFinite(m.loss_pct) && m.loss_pct >= 50 ? false : null,
  ];
  return checks.some((c) => c === false) ? false : true;
}

const score = (s) => (s ? `${s.ok} / ${s.total}` : "—");
const tone = (s) => (!s ? "" : s.ok === s.total ? "ok" : s.total - s.ok > 1 ? "bad" : "");
const first = (s) => (s?.bad?.length ? s.bad[0] : "усе відповідає");

// Телеметрія точки числами. Смужка overseer каже, КОЛИ ставало погано;
// ці рядки кажуть, ЯК зараз — щоб не йти по це на сторінку точки
// (docs/admin_panel.md, «телеметрія кожної POS»).
const num = (v, digits = 0) =>
  v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? null : Number(v).toFixed(digits);
const bits = (...parts) => parts.filter(Boolean).join(", ") || null;

// Один рядок даних під точкою. Смужки тут немає навмисно — історію цих
// чисел малює сторінка точки, — але колонки ті самі, що в рядків зі
// смужкою: числа займають її місце, а підпис справа лишається на місці.
const DataRow = ({ label, ok, value, at, note }) => (
  <BeatRow ok={ok ?? null} name={<span className="muted">{label}</span>} sub value={note}>
    <span className="fill">
      {value ?? "немає даних"}
      {/* Свіжість проби ховається, поки вона свіжа: інакше кожен рядок
          тягнув би за собою «2 хв тому» і числа тонули б у датах. */}
      {at && Date.now() - new Date(at).getTime() > 15 * 60_000 && (
        <span className="muted">, проба {fmt.ago(at)}</span>
      )}
    </span>
  </BeatRow>
);

// Кавомашина: на звʼязку, без несправностей і дефіциту, замовлення
// сходяться з чеками. Сесія порталу злетіла — судити нема з чого (null).
function jetOk(jet) {
  const m = jet?.metrics;
  if (!m || m.session === false) return null;
  return Boolean(m.online) && !m.fault_codes?.length && !Number(m.supply_short) && m.reconcile_ok !== false;
}
function jetSummary(m) {
  if (m.session === false) return "сесія порталу злетіла — потрібен вхід";
  return bits(
    m.online ? "онлайн" : "офлайн",
    m.fault_codes?.length ? `несправності: ${m.fault_codes.join("; ")}` : "без несправностей",
    Number(m.supply_short) ? `дефіцит інгредієнтів: ${m.supply_short}` : null,
    m.reconcile_ok === false ? "замовлення не сходяться з чеками" : null
  );
}

function PointTelemetry({ point }) {
  const pi = point.telemetry?.pi;
  const m = pi?.metrics ?? {};
  const at = pi?.measured_at ?? null;
  const jet = point.telemetry?.jetinno;

  const loss = num(m.loss_pct);
  const disk = num(m.disk_free_mb) === null ? null : (Number(m.disk_free_mb) / 1024).toFixed(1);
  const fps = num(m.kiosk_fps);

  // Малина шле пробу раз на пʼять хвилин. Протухла проба лишається на
  // екрані — вона все одно остання, що ми знаємо, — але крапка біля неї
  // гасне: зелене «втрати 0 %» годинної давнини гірше за відверте «не знаю».
  const fresh = at !== null && Date.now() - new Date(at).getTime() < 15 * 60_000;
  const dot = (value) => (fresh ? value : null);

  return (
    <>
      {/* Інтернет — не те саме, що «точка озивається»: проба з чергою
          доїжджає й після обриву, а ping із втратами показує зв'язок, який
          ще живий, але вже поганий. */}
      <DataRow
        label="пінг до сервера"
        note="5 пакетів до api; червоне — від двох загублених"
        ok={dot(loss === null ? null : Number(loss) < LOSS_BAD_PCT)}
        at={at}
        value={bits(
          m.uplink && `через ${UPLINK[m.uplink] ?? m.uplink}`,
          num(m.ping_ms) && `пінг ${num(m.ping_ms)} мс`,
          num(m.jitter_ms) && `jitter ${num(m.jitter_ms)} мс`,
          loss !== null && `втрати ${loss} %`
        )}
      />
      {/* Зв'язок — це лише про малину: вимкнений екран чи мертвий потік із
          «точка озивається» не видно. Але історія тут ні до чого — на
          дашборді потрібен поточний стан, а тижневі смужки по кожному
          булевому показнику живуть на сторінці точки (прохання власника
          24.09.2026). */}
      {[["монітор", point.monitor, "екран кіоска"], ["відеопотік", point.video, "камера точки"],
        ["розетка", point.plug, "Tuya, раз на пів години"]]
        .map(([label, s, note]) => s && (
          <DataRow key={label} label={label} ok={s.ok} note={note} value={s.detail ?? "працює"} />
        ))}
      <DataRow
        label="залізо"
        note="малина"
        // Пороги грубі навмисно: 80 °C — throttling малини, менше гігабайта
        // вільного — місце під релізи й чергу телеметрії.
        ok={dot(num(m.temp_c) === null ? null : Number(m.temp_c) < 80 && Number(m.disk_free_mb ?? 9e9) > 1024)}
        at={at}
        value={bits(
          num(m.temp_c, 1) && `${num(m.temp_c, 1)} °C`,
          num(m.cpu) && `CPU ${num(m.cpu)} %`,
          num(m.mem_used_mb) && `памʼять ${num(m.mem_used_mb)} МБ`,
          disk && `диск ${disk} ГБ`
        )}
      />
      <DataRow
        label="кіоск"
        note="меню на екрані"
        ok={dot(fps === null ? null : Number(fps) > 0)}
        at={at}
        value={bits(fps !== null && `${fps} fps`, m.release && `реліз ${m.release}`)}
      />
      {/* Підсумок кавомашини — тими самими правилами, що й блок Jetinno на
          сторінці точки (Pos.jsx): досі рядок був зеленим за будь-якої проби
          й показував перші шість сирих ключів (18.10.2026). */}
      <DataRow
        label="автомат"
        note="Jetinno, з порталу раз на пів години"
        ok={jetOk(jet)}
        at={jet?.measured_at ?? null}
        value={jet ? jetSummary(jet.metrics ?? {}) : "телеметрії ще немає"}
      />
    </>
  );
}

export function Health() {
  const [range, setRange] = useRange();
  const { data, error, loading, reload } = useData(() => api.health(range), [range]);
  const span = `за ${rangeLabel(range) === "доба" ? "добу" : rangeLabel(range)}`;

  if (loading && !data) return <Empty>вантажимо…</Empty>;
  if (error) return <Empty>не вдалось прочитати стан: {error.message}</Empty>;

  const onlinePoints = data.points.filter((p) => pointOk(p)).length;
  const silent = data.points.find((p) => !p.ok);

  return (
    <>
      <div className="head">
        <div>
          <h1>Дашборд здоровʼя</h1>
          <p>
            {data.updated_at ? `Оновлено ${fmt.ago(data.updated_at)}, історія ${span}` : "Проб ще не було: їх збирає overseer"}
          </p>
        </div>
        <div className="right">
          <RangePicker value={range} onChange={setRange} />
          <button className="btn" onClick={reload}>Оновити</button>
          <span className="env"><i />prod</span>
        </div>
      </div>

      <div className="grid k4" style={{ marginBottom: 12 }}>
        <Kpi label="Мікросервіси" value={score(data.score.services)} tone={tone(data.score.services)} note={first(data.score.services)} />
        <Kpi label="Фронтенди" value={score(data.score.frontends)} tone={tone(data.score.frontends)} note={first(data.score.frontends)} />
        <Kpi label="Компоненти" value={score(data.score.components)} tone={tone(data.score.components)} note={first(data.score.components)} />
        <Kpi
          label="POS онлайн"
          value={`${onlinePoints} / ${data.points.length}`}
          tone={data.points.length && onlinePoints === data.points.length ? "ok" : "bad"}
          note={silent ? `${silent.name}: ${silent.detail ?? "мовчить"}` : "усі точки на звʼязку"}
        />
      </div>

      {/* Одна колонка: у дві смужка пульсу стискалась до нечитабельного,
          а поруч із нею ще й назва та затримка. */}
      <div className="stack">
        <Card title="Мікросервіси" note={span}>
          {data.services.map((s) => (
            <BeatRow key={s.target} ok={s.ok} name={s.title} note={s.note} history={s.history} ms={s.ms ?? null} value={s.detail ?? s.note} />
          ))}
          <BeatScale history={data.services[0]?.history} />
        </Card>
        <Card title="Фронтенди" note={`${span}, час відповіді`}>
          {data.frontends.map((s) => (
            <BeatRow key={s.target} ok={s.ok} name={s.title} note={s.note} history={s.history} ms={s.ms ?? null} value={s.detail ?? s.note} />
          ))}
          <BeatScale history={data.frontends[0]?.history} />
        </Card>
        <Card title="Компоненти" note={span}>
          {data.components.map((s) => (
            <BeatRow key={s.target} ok={s.ok} name={s.title} note={s.note} history={s.history} ms={s.ms ?? null} value={s.detail ?? s.note} />
          ))}
          <BeatScale history={data.components[0]?.history} />
        </Card>
      </div>

      <Card title="Телеметрія POS" note={`${span}, крок ${data.step >= 3600_000 ? `${data.step / 3600_000} год` : `${(data.step ?? 1800_000) / 60_000} хв`}`} className="" style={{ marginTop: 12 }}>
        {data.points.length === 0 ? (
          <Empty>жодної живої точки в базі</Empty>
        ) : (
          data.points.map((p) => (
            <Fragment key={p.id}>
            <BeatRow
              ok={pointOk(p)}
              name={
                <span>
                  {p.name}
                  <small className="muted" style={{ display: "block", fontWeight: 400 }}>{p.short_address ?? p.address ?? p.id}</small>
                </span>
              }
              history={p.history}
              value={p.last_seen_at ? `озивалась ${fmt.ago(p.last_seen_at)}` : "не озивалась жодного разу"}
              onClick={() => go(`pos/${p.id}`)}
            />
            <PointTelemetry point={p} />
            </Fragment>
          ))
        )}
        <BeatScale history={data.points[0]?.history} />
      </Card>

      {/* Залізо самого дроплета — окремим розділом у кінці (03.10.2026):
          смужки вище відповідають «живе / ні», а тут — чи не впирається
          воно в стелю. */}
      <ServerSection range={range} span={span} />

      {/* Запити застосунку до api — теж стан сервісу, тож тут, а не в
          аналітиці гравців (18.10.2026). */}
      <ApiTech />
    </>
  );
}
